import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, MapPinIcon, ClockIcon, UsersIcon, SparklesIcon, MailCheckIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const FLYER_IMAGE = 'https://cdn.magicpatterns.com/uploads/hNncZZyyQBicW536oFFq4C/Encuentro_Catracho_Miami.png';
const MIAMI_BACKDROP = 'https://cdn.magicpatterns.com/patterns/generated-images/6c06614c-bc8f-4f97-bc32-9053989f4055.jpg';
const CONTACT_EMAIL = 'eva@honduranamerican.org';
const DETAIL_ICONS = [CalendarIcon, ClockIcon, MapPinIcon, UsersIcon];
const COPY = {
  ES: {
    back: 'Volver al sitio',
    eyebrow: 'Independencia de Honduras · Miami',
    subtitle: 'Celebremos Honduras · Conectemos Nuestra Comunidad',
    details: [{
      label: 'Fecha',
      value: 'Martes, 15 de septiembre de 2026'
    }, {
      label: 'Hora',
      value: '6:00 – 8:00 P.M.'
    }, {
      label: 'Lugar',
      value: 'Extreme Woodworks · 1520 NW 79th Ave, Doral, FL 33126'
    }, {
      label: 'Cupos',
      value: 'Limitados · Invitación especial'
    }],
    flyerAria: 'Ver la invitación completa',
    flyerAlt: 'Encuentro Catracho Miami — Martes 15 de septiembre de 2026, 6:00 a 8:00 P.M., Extreme Woodworks, Doral, FL',
    rsvpTitle: 'Confirmar Asistencia',
    rsvpText: 'Cupos limitados. Reserve su lugar a continuación.',
    rsvpFrameTitle: 'Encuentro Catracho Miami — Reserve su lugar',
    contactLead: 'Para más información, contacte a ',
    aboutEyebrow: 'Sobre la Noche',
    aboutHeading: 'Una Noche Para Reencontrarnos',
    aboutParagraphs: ['Encuentro Catracho reúne a hondureños del sur de Florida en una velada pensada para celebrar quiénes somos y de dónde venimos, en el mes que conmemora la independencia de nuestro país.', 'Más allá de la celebración, es una oportunidad para conocer a otros profesionales, empresarios y emprendedores hondureños, intercambiar ideas y descubrir cómo podemos apoyarnos mutuamente.', 'Nuestra meta es sencilla y ambiciosa a la vez: comenzar a construir una comunidad hondureña más conectada, más visible y más fuerte en Miami.'],
    highlights: [{
      title: 'Orgullo Catracho',
      text: 'Una noche para celebrar la Independencia de Honduras, nuestras raíces, nuestra cultura y el orgullo de ser hondureños lejos de casa.'
    }, {
      title: 'Una Noche Íntima',
      text: 'Un encuentro cercano entre hondureños, profesionales, empresarios, emprendedores y líderes de nuestra comunidad en el sur de Florida.'
    }, {
      title: 'Comunidad Más Unida',
      text: 'Creada para conocernos, conectar y comenzar a fortalecer juntos una comunidad hondureña más unida en Miami.'
    }],
    missionHeading: 'Conectar. Empoderar. Celebrar.',
    missionText: 'En la Asociación Hondureño-Americana, nuestra misión es Conectar, Empoderar y Celebrar. Esta noche es esa misión en acción: reunir a personas extraordinarias y crear vínculos que abran paso a nuevas ideas, nuevas colaboraciones y un impacto duradero.',
    missionClosing: 'Celebremos Honduras. Conectemos nuestra comunidad.',
    footer: 'Organizado por la Asociación Hondureño-Americana'
  },
  EN: {
    back: 'Back to site',
    eyebrow: 'Honduran Independence · Miami',
    subtitle: 'Celebrate Honduras · Connect Our Community',
    details: [{
      label: 'Date',
      value: 'Tuesday, September 15, 2026'
    }, {
      label: 'Time',
      value: '6:00 – 8:00 PM'
    }, {
      label: 'Venue',
      value: 'Extreme Woodworks · 1520 NW 79th Ave, Doral, FL 33126'
    }, {
      label: 'Seating',
      value: 'Limited · By special invitation'
    }],
    flyerAria: 'View the full invitation graphic',
    flyerAlt: 'Encuentro Catracho Miami — Tuesday, September 15, 2026, 6:00 to 8:00 PM, Extreme Woodworks, Doral, FL',
    rsvpTitle: 'Confirm Your Attendance',
    rsvpText: 'Seating is limited. Reserve your place below.',
    rsvpFrameTitle: 'Encuentro Catracho Miami — Reserve your place',
    contactLead: 'For more information, contact ',
    aboutEyebrow: 'About the Evening',
    aboutHeading: 'A Night to Come Together',
    aboutParagraphs: ['Encuentro Catracho brings together Hondurans from across South Florida for an evening created to celebrate who we are and where we come from, during the month that commemorates our country\u2019s independence.', 'Beyond the celebration, it is an opportunity to meet other Honduran professionals, business owners, and entrepreneurs, exchange ideas, and discover how we can support one another.', 'Our goal is both simple and ambitious: to begin building a Honduran community in Miami that is more connected, more visible, and stronger together.'],
    highlights: [{
      title: 'Honduran Pride',
      text: 'A night to celebrate Honduran Independence, our roots, our culture, and the pride of being Honduran far from home.'
    }, {
      title: 'An Intimate Evening',
      text: 'A close gathering of Hondurans — professionals, business owners, entrepreneurs, and community leaders across South Florida.'
    }, {
      title: 'A More United Community',
      text: 'Created so we can meet one another, connect, and begin strengthening a more united Honduran community in Miami.'
    }],
    missionHeading: 'Connect. Empower. Celebrate.',
    missionText: 'At the Honduran-American Association, our mission is to Connect, Empower, and Celebrate. This evening is that mission in action — bringing remarkable people together and creating connections that open the way to new ideas, new collaborations, and lasting impact.',
    missionClosing: 'Celebrate Honduras. Connect our community.',
    footer: 'Hosted by the Honduran-American Association'
  }
};
export function EncuentroCatrachoMiamiPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  const c = isEs ? COPY.ES : COPY.EN;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full bg-[#0b1f3f] text-white font-nunito" data-id="element-3604" data-mp="ioycigxbw8ot">
      <header className="relative z-10 w-full px-6 py-6 sm:px-12 flex items-center justify-between gap-4 border-b border-[#c9a227]/30" data-id="element-3605" data-mp="eph53hycd1h0">
        <a href="#" className="flex items-center gap-3 group" aria-label="Honduran American Association" data-id="element-3606" data-mp="3n1v1gfk56dps">
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain brightness-0 invert group-hover:opacity-80 transition-opacity" data-id="element-3607" data-mp="3vwj746tmpxf4" />
        </a>
        <div className="flex items-center gap-3" data-id="element-3608" data-mp="2zdbxudc1ep41">
          <button onClick={() => setLanguage(isEs ? 'EN' : 'ES')} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a227] hover:bg-[#dcb63b] text-[#0b1f3f] text-sm font-bold transition-colors" aria-label={isEs ? 'Switch to English' : 'Ver en español'} data-id="element-3609" data-mp="2zckafpslfxou">
            <GlobeIcon className="w-4 h-4" data-id="element-3610" data-mp="2kb3oili9ldtx" />
            {isEs ? 'English' : 'Español'}
          </button>
          <a href="#" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-sm font-semibold transition-colors" data-id="element-3611" data-mp="380em5u4bis3u">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3612" data-mp="2y1qftslp41fz" />
            <span className="hidden sm:inline" data-id="element-3613" data-mp="2y54fgt2z5wjq">{c.back}</span>
          </a>
        </div>
      </header>

      <main data-id="element-3614" data-mp="333o26vpe7cd5">
        {/* Invitation */}
        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-12 lg:pt-16" data-id="element-3615" data-mp="1mmjw4htiugwl">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center max-w-3xl mx-auto" data-id="element-3616" data-mp="1rmy3ryr7nou3">
            <p className="font-nunito text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-4" data-id="element-3617" data-mp="uwdvqenkjfof">
              {c.eyebrow}
            </p>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-[0.08em] leading-[0.95] mb-3" data-id="element-3618" data-mp="1rlbqvxz8dfm4">
              Encuentro Catracho
            </h1>
            <p className="font-nunito text-white/80 text-sm uppercase tracking-[0.2em]" data-id="element-3619" data-mp="1hwlh3zxy8nck">
              {c.subtitle}
            </p>
            <div className="w-20 h-px bg-[#c9a227] mx-auto mt-7" data-id="element-3620" data-mp="1du6ur5v2n5gj" />
          </motion.div>

          {/* Info bar */}
          <dl className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/10" data-id="element-3621" data-mp="2aepchch209p2">
            {c.details.map((item, i) => {
            const Icon = DETAIL_ICONS[i];
            return <div key={item.label} className="bg-[#0b1f3f] px-5 py-5 flex items-start gap-3" data-id="element-3622" data-mp="3t7z7bmgzg5a8" data-mpi={i}>
                  <Icon className="w-4 h-4 text-[#c9a227] mt-0.5 flex-shrink-0" data-id="element-3623" data-mp="12wug72f86y0" />
                  <div data-id="element-3624" data-mp="64cdjtl1g3dr">
                    <dt className="font-nunito text-white/45 text-[10px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-3625" data-mp="64ujjhxi5vyv">
                      {item.label}
                    </dt>
                    <dd className="font-nunito text-white text-sm font-semibold leading-snug" data-id="element-3626" data-mp="g0fn3ki7z4aq">
                      {item.value}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </section>

        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-16" data-id="element-3627" data-mp="ttdnjl9irml5">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-stretch" data-id="element-3628" data-mp="yryqhsg5okda">
            {/* Left — flyer */}
            <motion.aside initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex" data-id="element-3629" data-mp="6ad5qkjjlbo2">
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="relative block w-full h-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-[#c9a227]/50 bg-white group" aria-label={c.flyerAria} data-id="element-3630" data-mp="p0bgx9imzkvx">
                <img src={FLYER_IMAGE} alt={c.flyerAlt} className="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-3631" data-mp="130m6884asrez" />
              </a>
            </motion.aside>

            {/* Right — RSVP */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex flex-col h-full" data-id="element-3632" data-mp="2x87tta1qixy8">
              <div className="rounded-2xl border border-[#c9a227]/40 bg-white/[0.04] overflow-hidden flex flex-col flex-1" data-id="element-3633" data-mp="3g5ivt2er45hd">
                <div className="px-6 pt-6 pb-4" data-id="element-3634" data-mp="3b8iqwutn18ru">
                  <h2 className="inline-flex items-center gap-2 font-bebas text-2xl tracking-wider mb-1.5" data-id="element-3635" data-mp="375cl1oaftjlw">
                    <MailCheckIcon className="w-5 h-5 text-[#c9a227]" data-id="element-3636" data-mp="325zmw7e48q40" />
                    {c.rsvpTitle}
                  </h2>
                  <p className="font-nunito text-white/65 text-sm leading-relaxed" data-id="element-3637" data-mp="1vsjsib9tor4s">
                    {c.rsvpText}
                  </p>
                </div>
                <div className="bg-white flex-1" data-id="element-3638" data-mp="1s8rsvnns0bqp">
                  <iframe src="https://givebutter.com/embed/c/encuentro-catracho-miami-sw65ye" title={c.rsvpFrameTitle} className="w-full h-full min-h-[560px] block border-0" allow="payment" data-id="element-3639" data-mp="1s6yji0ls3df0" />
                </div>
                <div className="px-6 py-4 border-t border-white/10" data-id="element-3640" data-mp="1nbt29f3bmmx9">
                  <p className="font-nunito text-white/60 text-sm leading-relaxed" data-id="element-3641" data-mp="19gz1tys9p0lm">
                    {c.contactLead}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#c9a227] font-semibold hover:underline" data-id="element-3642" data-mp="1ebcw1xv711op">
                      {CONTACT_EMAIL}
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* About the evening — Miami backdrop */}
        <section className="relative border-t border-white/10 overflow-hidden" data-id="element-3643" data-mp="vkll4rvk5387">
          <img src={MIAMI_BACKDROP} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" data-id="element-3644" data-mp="bz34mnmt8gww" />
          <div className="absolute inset-0 bg-[#0b1f3f]/55" data-id="element-3645" data-mp="kz8d713qplcn" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b1f3f] via-[#0b1f3f]/20 to-[#0b1f3f]" data-id="element-3646" data-mp="gvk60jj0srrx" />

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-24" data-id="element-3647" data-mp="uv31abqqg8mt">
            <div className="text-center max-w-3xl mx-auto" data-id="element-3648" data-mp="px9wnn52vmon">
              <p className="inline-flex items-center gap-2 font-nunito text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-5" data-id="element-3649" data-mp="punlzemw4r27">
                <SparklesIcon className="w-4 h-4" data-id="element-3650" data-mp="zmb9x5k8npyx" />
                {c.aboutEyebrow}
              </p>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight mb-4" data-id="element-3651" data-mp="1dlkra8k6kq4h">
                {c.aboutHeading}
              </h2>
              <div className="w-16 h-px bg-[#c9a227] mx-auto mb-8" data-id="element-3652" data-mp="18l6hakz0r63t" />
            </div>
            <div className="space-y-5 font-nunito text-white/80 text-base sm:text-lg leading-relaxed text-center max-w-3xl mx-auto" data-id="element-3653" data-mp="3337u6d19374j">
              {c.aboutParagraphs.map(text => <p key={text} data-id="element-3654" data-mp="3cukln3j4zva1">{text}</p>)}
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-14" data-id="element-3655" data-mp="3gv4j687mh0wp">
              {c.highlights.map((item, i) => <motion.div key={item.title} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1,
              ease: [0.16, 1, 0.3, 1]
            }} className="rounded-2xl border border-white/15 bg-[#0b1f3f]/70 backdrop-blur-sm p-7 text-center" data-id="element-3656" data-mp="2k5nf1xoafmx4" data-mpi={i}>
                  <div className="w-10 h-px bg-[#c9a227] mb-5 mx-auto" data-id="element-3657" data-mp="ikaza04opovw" />
                  <h3 className="font-bebas text-2xl tracking-wider mb-3" data-id="element-3658" data-mp="drrkizu9gnpi">
                    {item.title}
                  </h3>
                  <p className="font-nunito text-white/70 text-sm leading-relaxed" data-id="element-3659" data-mp="9pwn46fgauh0">
                    {item.text}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="border-t border-white/10 bg-white/[0.03]" data-id="element-3660" data-mp="3qyjk2m0ytbdi">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center" data-id="element-3661" data-mp="3r0w2t1ejrprm">
            <h2 className="font-bebas text-3xl sm:text-4xl tracking-wider leading-tight mb-5" data-id="element-3662" data-mp="3myso83wl0524">
              {c.missionHeading}
            </h2>
            <p className="font-nunito text-white/75 text-base sm:text-lg leading-relaxed mb-4" data-id="element-3663" data-mp="1l64hhkkqazhl">
              {c.missionText}
            </p>
            <p className="font-nunito text-white text-base sm:text-lg font-semibold leading-relaxed" data-id="element-3664" data-mp="3ensboa9zxm64">
              {c.missionClosing}
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 py-8 text-center" data-id="element-3665" data-mp="35p6v2ko34h7z">
        <p className="font-nunito text-white/45 text-xs uppercase tracking-[0.2em]" data-id="element-3666" data-mp="39oz6r68rz3hk">
          {c.footer}
        </p>
      </footer>
    </div>;
}